/**
 * Pure reducer for the Model_Router state machine.
 *
 * Applies a `RouterEvent` to a `RouterState` and returns a new state. Every
 * event appends a single entry to a 200-entry ring-buffer log
 * (Requirement 5.11 / Property 16); per-event-kind effects on health are
 * documented inline below.
 *
 * No `Date.now`, no IO, no random — the reducer is fully deterministic and
 * uses the explicit `state.nowMs` clock injected by the caller.
 *
 * Validates: Properties 13 + 16 and Requirements 5.3, 5.4, 5.10, 5.11, 6.8.
 */

import type {
  ModelHealth,
  ModelId,
  RouterEvent,
  RouterLogEntry,
  RouterState,
} from './types';

/**
 * Maximum number of entries kept in `RouterState.log`.
 *
 * Per Requirement 5.11 / Property 16, the diagnostics buffer is bounded
 * to the most recent 200 entries; older entries are dropped when the
 * cap is exceeded.
 */
export const ROUTER_LOG_CAP = 200;

/**
 * Default cooldown duration applied on a `rateLimit` event when the API
 * does not provide a `Retry-After` value (Requirement 5.3).
 *
 * Reduced from 60s to 15s: a hard 1-minute lockout on every 429 was
 * too aggressive and caused the chain to feel stuck. The router still
 * honors any longer `Retry-After` header from the API.
 */
export const DEFAULT_RATE_LIMIT_COOLDOWN_MS = 15_000;

/**
 * Replace every literal occurrence of `apiKey` in `input` with
 * `'[REDACTED]'`. Used by `reduce` to scrub any accidental key
 * substring out of log `details` strings before they are appended
 * (Requirement 6.8 / Property 16).
 *
 * Pure: returns `input` unchanged when `apiKey` is `null` or empty.
 */
export function redactApiKey(input: string, apiKey: string | null): string {
  if (apiKey === null || apiKey === '') {
    return input;
  }
  // split/join performs a literal (non-regex) replace-all without needing
  // to escape regex metacharacters that may legitimately appear in keys.
  return input.split(apiKey).join('[REDACTED]');
}

/**
 * Append `entry` to `log` and return a new array truncated to the most
 * recent `ROUTER_LOG_CAP` entries. Pure — never mutates the input.
 *
 * The oldest entries are dropped from the front when the cap is
 * exceeded, so the returned array is always the chronological tail
 * of `[...log, entry]` of length `min(log.length + 1, ROUTER_LOG_CAP)`.
 */
export function appendLog(
  log: RouterLogEntry[],
  entry: RouterLogEntry,
): RouterLogEntry[] {
  const next = [...log, entry];
  if (next.length <= ROUTER_LOG_CAP) {
    return next;
  }
  return next.slice(next.length - ROUTER_LOG_CAP);
}

/**
 * Apply `event` to `state` and return a new `RouterState`.
 *
 * Pure: never mutates the input state, never reads `Date.now`, performs
 * no IO. The caller is responsible for advancing `state.nowMs` via a
 * `tick` event whose `nowMs` is monotone non-decreasing — the reducer
 * trusts the caller and does NOT enforce monotonicity by clamping.
 *
 * `apiKey` is the configured Gemini API key (or `null` when none is
 * configured). When provided, every `details` string written into the
 * log is run through `redactApiKey` so that even an accidental key
 * substring is replaced with `'[REDACTED]'` before the entry is
 * persisted (Requirement 6.8 / Property 16).
 *
 * Per-event behavior (Property 13):
 * - `request`     → log only; health unchanged.
 * - `rateLimit`   → set `health[modelId].cooldownUntilMs = nowMs +
 *                   (retryAfterSec ? retryAfterSec * 1000 : 60_000)`;
 *                   every other model's `cooldownUntilMs` is unchanged
 *                   (Requirement 5.3).
 * - `transient`   → log only; no cooldown is set for any model
 *                   (Requirement 5.4).
 * - `safetyBlock` → log only; health unchanged.
 * - `success`     → increment `requestsLastMinute` and `requestsLastDay`
 *                   for `modelId` only; counters for other models are
 *                   unchanged (Requirement 5.10).
 * - `tick`        → set `nowMs = event.nowMs`; health unchanged.
 *
 * Every branch appends one log entry whose `kind` mirrors the event
 * `kind`, whose `seq = lastSeq + 1`, and whose `timestampMs` is the
 * pre-event `state.nowMs` (Property 16).
 */
export function reduce(
  state: RouterState,
  event: RouterEvent,
  apiKey: string | null = null,
): RouterState {
  const seq = (state.log.at(-1)?.seq ?? 0) + 1;
  const timestampMs = state.nowMs;

  /**
   * Construct an immutable log entry, conditionally attaching
   * `modelId` and `details` to satisfy `exactOptionalPropertyTypes`.
   */
  const buildEntry = (
    kind: RouterLogEntry['kind'],
    modelId?: ModelId,
    details?: string,
  ): RouterLogEntry => {
    const entry: RouterLogEntry = { seq, timestampMs, kind };
    if (modelId !== undefined) {
      entry.modelId = modelId;
    }
    if (details !== undefined) {
      entry.details = redactApiKey(details, apiKey);
    }
    return entry;
  };

  switch (event.kind) {
    case 'request': {
      const entry = buildEntry('request');
      return {
        ...state,
        log: appendLog(state.log, entry),
      };
    }

    case 'rateLimit': {
      const cooldownMs = event.retryAfterSec
        ? event.retryAfterSec * 1000
        : DEFAULT_RATE_LIMIT_COOLDOWN_MS;
      const cooldownUntilMs = state.nowMs + cooldownMs;
      const nextHealth = updateHealth(state.health, event.modelId, (h) => ({
        ...h,
        cooldownUntilMs,
      }));
      const entry = buildEntry(
        'rateLimit',
        event.modelId,
        `cooldownMs=${cooldownMs}`,
      );
      return {
        ...state,
        health: nextHealth,
        log: appendLog(state.log, entry),
      };
    }

    case 'transient': {
      // No cooldown is set on transient errors (Requirement 5.4); the
      // executor handles its own 250 ms backoff outside the reducer.
      const entry = buildEntry('transient', event.modelId);
      return {
        ...state,
        log: appendLog(state.log, entry),
      };
    }

    case 'safetyBlock': {
      const entry = buildEntry('safetyBlock', event.modelId);
      return {
        ...state,
        log: appendLog(state.log, entry),
      };
    }

    case 'success': {
      const nextHealth = updateHealth(state.health, event.modelId, (h) => ({
        ...h,
        requestsLastMinute: h.requestsLastMinute + 1,
        requestsLastDay: h.requestsLastDay + 1,
      }));
      const entry = buildEntry('success', event.modelId);
      return {
        ...state,
        health: nextHealth,
        log: appendLog(state.log, entry),
      };
    }

    case 'tick': {
      const entry = buildEntry('tick');
      return {
        ...state,
        nowMs: event.nowMs,
        log: appendLog(state.log, entry),
      };
    }

    default: {
      // Exhaustiveness guard — fails the type-check if a new event kind
      // is added to `RouterEvent` without a corresponding branch here.
      const _exhaustive: never = event;
      return _exhaustive;
    }
  }
}

/**
 * Return a new health record where the entry for `modelId` has been
 * replaced with `update(prev)`. Every other model's health is shared
 * by reference with the input (referential equality preserved), which
 * lets property tests assert "every other model's `cooldownUntilMs` is
 * unchanged" via simple `===` comparisons.
 *
 * If the `modelId` is somehow missing from the record (defensive — the
 * caller is expected to keep a fully-populated health map keyed by
 * every `ModelId`), the input is returned unchanged.
 */
function updateHealth(
  health: Record<ModelId, ModelHealth>,
  modelId: ModelId,
  update: (prev: ModelHealth) => ModelHealth,
): Record<ModelId, ModelHealth> {
  const prev = health[modelId];
  if (prev === undefined) {
    return health;
  }
  return { ...health, [modelId]: update(prev) };
}
